// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Wire types for the ReUseX GUI API.
 *
 * Hand-written mirror of the `components.schemas` section of
 * `docs/gui/openapi.yaml` (contract version 1.0.0). These are the *wire* shapes
 * — no convenience fields, no renaming — so that a diff against the spec is a
 * literal one. Anything derived belongs in the component that derives it.
 *
 * Optional (`?`) here means "the spec does not list it under `required`".
 */

/** `Error` — the body of every non-2xx response. */
export interface ApiError {
  error: string;
  status?: number;
  code?: ErrorCode;
}

/**
 * `ErrorCode` (ruxd-202): a stable, machine-readable reason for an error.
 * Branch on this, never on the wording of `error`. A code keeps its name and
 * meaning once shipped; new ones may be added at any time, so an unknown
 * string here is treated like a missing code (`isErrorCode` in `client.ts`
 * guards the cast at the one place a wire body is read).
 *
 * `auth_required` (401), `forbidden` (403) and `not_found` (404) also cover
 * every response of that status that names no more specific code. What each
 * code means and which status it comes with: ruxd's `docs/api/README.md`,
 * "Error codes".
 */
export type ErrorCode =
  // generic: any route
  | 'auth_required'
  | 'forbidden'
  | 'not_found'
  | 'invalid_json'
  | 'unknown_field'
  | 'missing_field'
  | 'invalid_field'
  | 'unsupported_media_type'
  | 'case_busy'
  | 'database_busy'
  | 'server_mode_required'
  | 'auth_session_required'
  // /me/speckle
  | 'speckle_account_required'
  | 'speckle_token_malformed'
  | 'speckle_token_invalid'
  | 'speckle_server_not_https'
  | 'speckle_server_malformed'
  | 'speckle_server_ip_address'
  | 'speckle_server_loopback'
  | 'speckle_server_not_allowed'
  | 'speckle_server_unreachable'
  | 'speckle_check_running'
  | 'speckle_check_capacity'
  | 'speckle_validator_unavailable'
  | 'speckle_storage_unavailable'
  | 'speckle_token_from_config'
  // imports and jobs
  | 'token_in_request'
  | 'import_stage_via_jobs'
  | 'invalid_parameters'
  | 'field_set_by_server'
  | 'speckle_no_token'
  | 'speckle_server_mismatch'
  | 'ifc_not_ifc'
  | 'ifc_too_large'
  | 'ifc_quota_exceeded'
  | 'ifc_upload_cap'
  | 'ifc_upload_completing'
  // chunked uploads (/uploads and /imports/ifc)
  | 'uploads_unavailable'
  | 'upload_too_large'
  | 'upload_chunk_too_large'
  | 'upload_past_size'
  | 'upload_offset_mismatch'
  | 'upload_incomplete'
  | 'upload_cap'
  | 'upload_no_space'
  // /panoramas
  | 'panorama_invalid_pose'
  | 'panorama_invalid_filename'
  | 'panorama_not_jpeg'
  | 'panorama_not_equirect'
  | 'panorama_too_large'
  | 'panorama_name_taken'
  // RTAB-Map session import + pose-graph editor (ruxd PR #19, ruxd-64g)
  | 'rtabmap_not_sqlite'
  | 'rtabmap_too_large'
  | 'rtabmap_quota_exceeded'
  | 'rtabmap_upload_cap'
  | 'rtabmap_upload_completing'
  | 'session_already_imported'
  | 'posegraph_edit_refused'
  | 'nothing_to_undo'
  | 'nothing_to_redo'
  // solid-model edits (ruxd-5uv)
  | 'model_conflict';

/** Every {@link ErrorCode}, in contract order — the runtime half of the type, for `isErrorCode`. */
export const ERROR_CODES = [
  'auth_required',
  'forbidden',
  'not_found',
  'invalid_json',
  'unknown_field',
  'missing_field',
  'invalid_field',
  'unsupported_media_type',
  'case_busy',
  'database_busy',
  'server_mode_required',
  'auth_session_required',
  'speckle_account_required',
  'speckle_token_malformed',
  'speckle_token_invalid',
  'speckle_server_not_https',
  'speckle_server_malformed',
  'speckle_server_ip_address',
  'speckle_server_loopback',
  'speckle_server_not_allowed',
  'speckle_server_unreachable',
  'speckle_check_running',
  'speckle_check_capacity',
  'speckle_validator_unavailable',
  'speckle_storage_unavailable',
  'speckle_token_from_config',
  'token_in_request',
  'import_stage_via_jobs',
  'invalid_parameters',
  'field_set_by_server',
  'speckle_no_token',
  'speckle_server_mismatch',
  'ifc_not_ifc',
  'ifc_too_large',
  'ifc_quota_exceeded',
  'ifc_upload_cap',
  'ifc_upload_completing',
  'uploads_unavailable',
  'upload_too_large',
  'upload_chunk_too_large',
  'upload_past_size',
  'upload_offset_mismatch',
  'upload_incomplete',
  'upload_cap',
  'upload_no_space',
  'panorama_invalid_pose',
  'panorama_invalid_filename',
  'panorama_not_jpeg',
  'panorama_not_equirect',
  'panorama_too_large',
  'panorama_name_taken',
  'rtabmap_not_sqlite',
  'rtabmap_too_large',
  'rtabmap_quota_exceeded',
  'rtabmap_upload_cap',
  'rtabmap_upload_completing',
  'session_already_imported',
  'posegraph_edit_refused',
  'nothing_to_undo',
  'nothing_to_redo',
  'model_conflict',
] as const satisfies readonly ErrorCode[];

/** `Health` — liveness probe and version handshake. */
export interface Health {
  status: 'ok';
  api_version: string;
  version: string;
  implementation?: 'rux-gui' | 'ruxd';
  /** The case id (`GET /cases/{cid}/health`). */
  case?: string;
  project: {
    name: string;
    open: boolean;
    schema_version?: number;
  };
}

/** `Case` — one project the server serves (`GET /api/v1/cases`). */
export interface CaseSummary {
  /** URL-safe id, the `{cid}` of every per-case route. */
  id: string;
  name: string;
  /** The `.rux` file name; never a server path. */
  file_name: string;
  /** ISO-8601 UTC. */
  created_at: string;
  archived: boolean;
  size_bytes: number;
  /** The server may delete it (it lives in the server's data dir). */
  deletable: boolean;
  /** Open on the server right now. */
  open: boolean;
  /**
   * The card's figures, read by the server without opening the case: the
   * building record and the survey summary/fractions (same shapes as
   * `/projects`, `/survey/summary`, `/survey/fractions`). `null` when the
   * project cannot be read; `survey` is `null` on an older schema until the
   * case is opened once.
   */
  summary?: CaseCardFigures | null;
  /**
   * The caller's role in this case (server mode); local mode's implicit user
   * owns every case. `null` never reaches a client (it could not see the
   * case), but an older server omits the field.
   */
  role?: CaseRole | null;
}

/** A member's role in one case (ruxd server mode, phase S3). */
export type CaseRole = 'viewer' | 'editor' | 'owner';

/**
 * `GlobalRole` — an account's role outside any case (ruxd-wmc.1).
 * user: the cases they are given, creating cases. group_manager: also
 * creating groups and managing their members. admin: everything everywhere.
 */
export type GlobalRole = 'user' | 'group_manager' | 'admin';

/** `ShareRole` — what a case's share with a group gives its members (a group never owns a case). */
export type ShareRole = 'viewer' | 'editor';

/** `UserBrief` — an account as a list shows it. */
export interface UserBrief {
  id: string;
  email: string;
  /** The username (`user_metadata.username`); empty when none. */
  display_name: string;
}

/** `User` — an account (never its password). */
export interface AuthUser {
  /**
   * A Supabase user id (uuid); the nil uuid for local mode's and the
   * superuser's synthetic user and for a share link.
   */
  id: string;
  /** Empty for a share link. */
  email: string;
  /** The username; "local"/"superuser" for the synthetic users; a share link's label. */
  display_name: string;
  role: GlobalRole;
  /** `role` is `admin` (kept for older clients). */
  is_admin: boolean;
  disabled: boolean;
  created_at: string;
}

/** `AuthMe` — `GET /api/v1/auth/me` and the answer to a login. */
export interface AuthMe {
  /** `local`: `ruxd --local`, one implicit user, no login. */
  mode: 'local' | 'server';
  /** How this request authenticated. */
  via: 'local' | 'session' | 'token' | 'superuser' | 'share_link' | 'anonymous';
  user: AuthUser;
  /** An API token, or a share link, limited to this case. */
  case_scope?: string;
  /**
   * The ruxd browser session behind this request (cookie auth only). Additive;
   * an older ruxd omits it. `expires_at`: ISO-8601 UTC, when the session ends
   * (its sliding expiry, capped at 12 h after the exchange).
   */
  session?: { expires_at?: string };
  /** `via: share_link` only: the link (with `id` and `case`). */
  share_link?: ShareLinkPublic;
}

/** `Group` (ruxd-wmc.2) — a flat group of users: no leader, no roles inside it. */
export interface Group {
  id: string;
  name: string;
  created_at: string;
  member_count: number;
  /** Whether the caller is in the group. */
  is_member: boolean;
}

/** `GroupMember`. */
export interface GroupMember {
  user: UserBrief;
}

/** `CaseGroupShare` — `GET /cases/{cid}/groups` (ruxd-wmc.3). */
export interface CaseGroupShare {
  group: Group;
  role: ShareRole;
}

/** `GroupCase` — a case shared with a group, `GET /groups/{gid}/cases`. */
export interface GroupCase {
  id: string;
  name: string;
  role: ShareRole;
  /** The caller's effective role in the case; `null` when they have none (an admin). */
  my_role: CaseRole | null;
}

/** `ShareLink` — as a case's owners and admins see it; never the token (ruxd-wmc.4). */
export interface ShareLink {
  id: number;
  case: string;
  label: string;
  has_password: boolean;
  /** `null`: never. */
  expires_at: string | null;
  created_at: string;
  /** `null`: the superuser, or a deleted account. */
  created_by: UserBrief | null;
  revoked_at: string | null;
  last_used_at: string | null;
  state: 'active' | 'expired' | 'revoked';
}

/** `ShareLinkCreated` — the token is in this answer only. */
export interface ShareLinkCreated extends ShareLink {
  /** `rxl_…` */
  token: string;
}

/** `ShareLinkPublic` — what a link's holder may know; `id`/`case` only once opened. */
export interface ShareLinkPublic {
  id?: number;
  case?: string;
  label: string;
  requires_password: boolean;
  expires_at: string | null;
}

/** `ApiToken` — one of your API tokens (never the token itself). */
export interface ApiToken {
  id: number;
  name: string;
  /** Limited to this case, or null for every case you may see. */
  case: string | null;
  created_at: string;
  /** null = never expires. */
  expires_at: string | null;
  last_used_at: string | null;
}

/** Where a Speckle token comes from (`SpeckleTokenStatus.source`). */
export type SpeckleTokenSource = 'account' | 'runtime' | 'config';

/**
 * `SpeckleTokenStatus` — `GET`/`PUT /api/v1/me/speckle` (ruxd-fk8.4): your
 * Speckle personal access token's state. Never the token itself.
 */
export interface SpeckleTokenStatus {
  configured: boolean;
  /** The token's server; the default server when none is set. */
  server: string;
  /** `…` and the token's last four characters (`…` alone for a short token); null when none is set. */
  token_hint: string | null;
  /** When it was stored; null when none is set or it comes from the configuration. */
  updated_at: string | null;
  /**
   * `account`: your stored token (server mode). `runtime`: PUT in local mode,
   * until ruxd stops. `config`: local mode's `--speckle-token[-file]` /
   * `$SPECKLE_TOKEN`. null when none is set.
   */
  source: SpeckleTokenSource | null;
  /** The Speckle account the token was checked as; null for a configured token or none. */
  account: { id: string; name: string } | null;
  /** Server mode: false when the token's server was taken off the allowlist (the token is then not used). */
  server_allowed: boolean;
  /** Server mode: the servers a token may be for; null in local mode (any https server, http to localhost). */
  allowed_servers: string[] | null;
}

/** `PUT /api/v1/me/speckle`'s body. `server` defaults to `https://app.speckle.systems`. */
export interface SpeckleTokenPut {
  token: string;
  server?: string;
}

/** The answer to creating a token: the token, shown once. */
export interface NewApiToken extends ApiToken {
  token: string;
}

/** `CaseMember` — `GET /api/v1/cases/{cid}/members`. */
export interface CaseMember {
  user: UserBrief;
  role: CaseRole;
}

export interface CaseCardFigures {
  project: ProjectInfo | null;
  survey: SurveySummary | null;
  fractions: SurveyFractions | null;
}

/** `CaseList` — `GET /api/v1/cases`. Not paged: a server's list is short. */
export interface CaseList {
  cases: CaseSummary[];
  /** Cases can be created and uploaded. */
  writable: boolean;
  upload: { max_bytes: number; chunk_bytes: number };
}

/** `Upload` — a chunked `.rux` upload in progress (`/api/v1/uploads`). */
export interface UploadSession {
  id: string;
  name: string;
  size: number;
  received: number;
  /** Largest chunk the server accepts. */
  chunk_bytes: number;
}

/**
 * `IfcUpload` — a chunked IFC upload into one case
 * (`/api/v1/cases/{cid}/imports/ifc`, ruxd PR #12): an `Upload` plus the
 * largest file the server takes.
 */
export interface IfcUpload extends UploadSession {
  max_bytes: number;
}

/**
 * A chunked RTAB-Map session upload (`/api/v1/cases/{cid}/imports/rtabmap`,
 * ruxd PR #19): the `/imports/ifc` protocol and the same `IfcUpload` shape.
 */
export type RtabmapUpload = IfcUpload;

/**
 * `SpeckleImportRequest` — `POST /cases/{cid}/imports/speckle` (ruxd PR #12):
 * ReUseX's `import_speckle` parameters. Never a token: ruxd uses the
 * caller's own (`/me/speckle`). Omitted options take the library default.
 */
export interface SpeckleImportRequest extends IfcImportOptions {
  project: string;
  model: string;
  /** Omitted = the latest version. */
  version?: string;
  /** Omitted = the server the caller's token is for. */
  server?: string;
  /** Revit internal z minus level elevation [m]; omitted = measured. */
  elevation_offset?: number;
}

/** `IfcImportOptions` — `POST /cases/{cid}/imports/ifc/{id}/complete`'s body (ReUseX's `import_ifc` options). */
export interface IfcImportOptions {
  /** Store the per-category meshes (default true). */
  meshes?: boolean;
  /** Height of the top storey [m], 0.5–100 (default 3.0). */
  storey_height?: number;
  /** Thinner walls get no wall record [m], 0–2 (default 0.05). */
  min_wall_thickness?: number;
  min_wall_fill?: number;
  opening_host_tolerance?: number;
  recenter?: boolean;
  recenter_threshold?: number;
}

/** `EndpointInfo` — one row of the server's self-described route table. */
export interface EndpointInfo {
  method: string;
  path: string;
  summary: string;
  binary?: boolean;
}

/** `ProjectInfo` — a project metadata record. */
export interface ProjectInfo {
  id: string;
  name: string;
  building_address?: string;
  /** 0 means "not set". */
  year_of_construction?: number;
  survey_date?: string;
  survey_organisation?: string;
  notes?: string;
  // The BEK 496 Sagsdata fields (ruxd-6tz, CONTRACT-GAPS gap 3). The
  // contract returns every one, null when unset; a server from before it
  // omits them, which `bygning/sagsdata.ts` detects per key.
  /** BBR-nummer. */
  bbr_number?: string | null;
  /** Matrikel. */
  cadastral?: string | null;
  /** Bygherre. */
  client?: string | null;
  /** Bygherrens kontaktperson. */
  client_contact?: string | null;
  /** Miljø- og ressourcekoordinator (MRK). */
  coordinator?: string | null;
  /** Kommune. */
  municipality?: string | null;
  /** Nedrivningsstart, `YYYY-MM-DD`. */
  demolition_start?: string | null;
  /** Nedrevet etageareal, m². */
  demolished_floor_area_m2?: number | null;
  /** When the bygherre approved the screening: a date or an ISO 8601 date-time, returned as sent. */
  screening_approved_at?: string | null;
}

/** `WorldPoint` — `[x, y, z]` in the project's world frame, metres. */
export type WorldPoint = [number, number, number];

/** `WorldBox` — `[[min x, min y, min z], [max x, max y, max z]]`, world coordinates. */
export type WorldBox = [WorldPoint, WorldPoint];

/** `Storey` — one stored storey (`GET /storeys`), lowest first. */
export interface Storey {
  /** StoreyRecord.idx — 0 for the lowest storey; the key rooms and parts use. */
  id: number;
  guid: string;
  name: string;
  /** World z of the floor, metres. */
  z_min: number;
  /** World z of the ceiling, metres. */
  z_max: number;
  /** Room ids (`Room.id`) on this storey, ascending. */
  room_ids: number[];
  source: 'detected' | 'manual' | 'imported';
  /** A locked storey survives re-detection. */
  locked: boolean;
  /** Written by a model edit (as `SolidCommon.edited`); absent from a server before ruxd-5uv. */
  edited?: boolean;
}

/** `Room` — one room of the `rooms` label cloud (`GET /rooms`). */
export interface Room {
  /** Label value in the `rooms` cloud (= `SurveyPart.room_id`). */
  id: number;
  name: string;
  /** `Storey.id` the room stands on; null when no storeys are stored. */
  storey_id: number | null;
  centroid: WorldPoint;
  bbox: WorldBox;
  point_count: number;
}

export type CloudType = 'PointXYZRGB' | 'Normal' | 'Label' | 'PointXYZ';

/** `CloudInfo` — one named point cloud. */
export interface CloudInfo {
  name: string;
  type: CloudType;
  point_count: number;
  width: number;
  height: number;
  organized: boolean;
  /**
   * Present for `Label` clouds only. Classifies the cloud as structural
   * segmentation (`"geometry"`: planes, rooms) or object-class annotation
   * (`"semantic"`: instances, annotation-derived). Server-authoritative —
   * the client must not re-derive this from the name.
   */
  label_kind?: 'geometry' | 'semantic';
  /**
   * Label id → name, for `Label` clouds only. Key `"0"` is never present:
   * 0 means unlabeled (STANDARDS §3).
   */
  labels?: Record<string, string>;
}

/** `CloudPointsPage` — one page of point rows. */
export interface CloudPointsPage {
  name: string;
  type: string;
  offset: number;
  /** Points in *this* page. */
  count: number;
  /** Points in the whole cloud. */
  total: number;
  /** Field names, in the order they appear in each row of `points`. */
  fields: string[];
  points: number[][];
  /**
   * Present only on a `max_points` request (#320). True when the rows are a
   * voxel-subsampled view of the whole cloud rather than a window of it;
   * false when the cloud already fit the budget and came back complete.
   */
  lod?: boolean;
  /** Voxel edge the subsample settled on, present only when `lod` is true. */
  voxel_size?: number;
}

/** `TileInfo` — one spatial tile's AABB and point count (#395). */
export interface TileInfo {
  id: number;
  count: number;
  min: [number, number, number];
  max: [number, number, number];
}

/**
 * `CloudTileIndex` — the spatial tile index of one cloud (#395).
 *
 * Present only for clouds stored in morton_10bit_bitrev order. Tile `k`
 * contains the points where `(sort_key & (K-1)) == k`, where
 * `sort_key = reverse_bits30(morton(x,y,z))` and `K = tile_count`. The server
 * selects them with an O(N) scan at serve time.
 */
export interface CloudTileIndex {
  name: string;
  tile_count: number;
  tile_bits: number;
  point_count: number;
  tiles: TileInfo[];
}

/** `MeshInfo` — one stored mesh. */
export interface MeshInfo {
  name: string;
  format?: 'ply' | 'obj';
  vertex_count: number;
  face_count: number;
  stage?: string;
  parameters?: string;
  created_at?: string;
  texture_count?: number;
}

/**
 * `GsplatInfo` — one Gaussian splat stored in the project (#322).
 *
 * Produced by `rux create gsplat` (or brought in with `rux import gsplat`) and
 * stored in the `.rux` like a mesh, so this is metadata about a project row,
 * never a filesystem path.
 */
export interface GsplatInfo {
  name: string;
  /** Storage format of the blob. `ply` is the INRIA 3DGS layout. */
  format: 'ply';
  gaussian_count: number;
  /** 0 means view-independent colour, not "missing". */
  sh_degree: number;
  byte_size: number;
  stage?: string;
  parameters?: string;
  created_at?: string;
}

/** `TextureInfo` — texture metadata (never the image bytes). */
export interface TextureInfo {
  tex_name: string;
  format: string;
  width: number;
  height: number;
}

/** `ScanGroup` — one import session's frames within a `FrameList` (#462). */
export interface ScanGroup {
  scan_id: number;
  /** Raw path passed to `rux import`; take the final segment for display. */
  source_path: string;
  imported_at: string;
  /** Filtered frame ids belonging to this scan, in ascending order. */
  ids: number[];
}

/** `FrameList` — sensor frame ids plus aggregate counts. */
export interface FrameList {
  ids: number[];
  total_count: number;
  segmented_count: number;
  width?: number;
  height?: number;
  /**
   * Per-scan breakdown of the ids above (#462).
   * Absent for projects imported before schema v17.
   */
  scans?: ScanGroup[];
}

/** `Intrinsics` — pinhole intrinsics of one sensor. */
export interface Intrinsics {
  fx: number;
  fy: number;
  cx: number;
  cy: number;
  width: number;
  height: number;
  /** Row-major 4x4 camera-to-base transform. */
  local_transform?: number[];
}

/** `FrameInfo` — one sensor frame. */
export interface FrameInfo {
  id: number;
  /** Epoch seconds; -1 when unknown. */
  timestamp?: number;
  /**
   * Row-major 4x4 world pose, verbatim as stored. A frame with no stored pose
   * reads back as identity — check `has_pose` before trusting it.
   */
  pose: number[];
  /** Whether `pose` is a usable stored pose rather than the identity fallback. */
  has_pose?: boolean;
  intrinsics?: Intrinsics;
  has_depth?: boolean;
  has_confidence?: boolean;
  has_segmentation: boolean;
}

export type FrameImageKind = 'color' | 'depth' | 'confidence' | 'segmentation';

/** `PanoramaInfo` — one 360 panorama with its pose provenance. */
export interface PanoramaInfo {
  id: number;
  filename: string;
  timestamp?: number;
  /** Matched sensor frame, -1 if unmatched. */
  node_id: number;
  /** True once `rux align 360` has resected this panorama or it was placed by hand. */
  has_pose: boolean;
  /**
   * Row-major 4x4 camera-to-world pose from content-based alignment or a
   * manual placement — **identity until `rux align 360` has run or it was
   * placed by hand**. Check `has_pose` first.
   */
  pose?: number[];
  /**
   * `timestamp` — no pose of its own (only `frame_pose`); `aligned` — `rux
   * align 360`; `manual` — placed by hand (`PATCH` / `POST /panoramas`,
   * `rux set panoramas.<name>.pose`, `rux import 360 --pose`).
   */
  pose_source: PanoramaPoseSource;
  /**
   * Heading of the pose the panorama is shown at, degrees in (-180, 180]
   * about world +z, 0 = +x, counter-clockwise seen from above (ReUseX
   * `core::panorama_yaw_deg`); null with no placement at all. Optional only
   * because a server before ruxd#11 omits it.
   */
  yaw_deg?: number | null;
  /**
   * `Storey.id` the position is on (the nearest storey's between bands);
   * null without storeys or a placement. Optional only for a server before
   * ruxd#11.
   */
  storey_id?: number | null;
  align_inliers?: number;
  /** Angular RMS of inlier bearings, degrees. -1 if unaligned. */
  align_rms?: number;
  /** Whether `frame_pose` is present. */
  has_frame_pose?: boolean;
  /**
   * Row-major 4x4 pose of the timestamp-matched sensor frame (`node_id`),
   * derived on read.
   *
   * A separate field from `pose` on purpose: a borrowed frame pose carries the
   * panorama's mounting offset and the timestamp-match error, and is a
   * different claim from a resected one.
   */
  frame_pose?: number[];
}

export type PanoramaPoseSource = 'timestamp' | 'aligned' | 'manual';

/**
 * Body of `PATCH /panoramas/{id}`: **either** a whole row-major 4×4 pose,
 * **or** a nudge of any of the world position components and the absolute
 * heading (the rest is kept). Never both.
 */
export type PanoramaPlacementPatch =
  | { pose: number[] }
  | { x?: number; y?: number; z?: number; yaw_deg?: number };

/** The query of `POST /panoramas` (the body is the raw equirect JPEG). */
export interface PanoramaUploadQuery {
  /** World position of the camera centre, metres. */
  x: number;
  y: number;
  z: number;
  /** Heading about world +z, degrees (default 0, see `PanoramaInfo.yaw_deg`). */
  yaw_deg?: number;
  /** Plain file name ending in .jpg/.jpeg, ≤ 255 bytes. */
  filename?: string;
  /** Capture time, epoch seconds. */
  timestamp?: number;
}

/** `PlanXY` — a world `[x, y]`, metres. */
export type PlanXY = [number, number];

/** `SolidCommon` — the fields every building-model row carries. */
export interface SolidCommon {
  /** ProjectDB row id. */
  id: number;
  /** Stable identity (IFC GlobalId when imported). */
  guid: string;
  source: 'detected' | 'manual' | 'imported';
  /** A locked row survives re-detection. */
  locked: boolean;
  /**
   * Written by a model edit (`POST /model/edits`): `metadata.edited` or
   * `metadata.edited_from` is set. Absent from a server before ruxd-5uv.
   */
  edited?: boolean;
  /** 0..1; null when unknown. */
  confidence: number | null;
  /** The row's metadata, parsed when it is JSON, else the text; null when empty. */
  metadata: unknown;
}

/** `SolidSpace` — one space (room) of the building model, world coordinates (`GET /spaces`). */
export interface SolidSpace extends SolidCommon {
  name: string;
  kind: 'room' | 'corridor' | 'open_plan' | 'unknown';
  /** `Storey.id` it is on; null when no stored storey has its idx. */
  storey_id: number | null;
  storey_guid: string | null;
  polygon: {
    /** Outer ring, world x/y — open (the first point is not repeated). */
    outer: PlanXY[];
    holes: PlanXY[][];
  };
  /** World z of its floor, metres. */
  z_floor: number;
  /** World z of its ceiling, metres. */
  z_ceiling: number;
  /** m²; null when unknown. */
  area: number | null;
  /** m; null when unknown. */
  perimeter: number | null;
  /** m³; null when unknown. */
  volume: number | null;
  /** Its label in the `rooms` cloud (= `Room.id`); null when none. */
  room_label: number | null;
}

/** `SolidWall` — one wall of the building model as its centre line (`GET /walls`). */
export interface SolidWall extends SolidCommon {
  /** `Storey.id` it stands on; null when no stored storey has its idx. */
  storey_id: number | null;
  storey_guid: string | null;
  start: PlanXY;
  end: PlanXY;
  /** World z of its base, metres. */
  z_base: number;
  /** World z of its top, metres. */
  z_top: number;
  height: number;
  /** Metres; null when unknown. */
  thickness: number | null;
  is_external: boolean;
  /** One-sided: the thickness is a default, not measured. */
  assumed: boolean;
}

/** `SolidOpening` — a door, window or opening on its host wall (`GET /openings`). */
export interface SolidOpening extends SolidCommon {
  kind: 'door' | 'window' | 'opening';
  state: 'open' | 'closed' | 'unknown';
  /** `SolidWall.guid` of its host. */
  wall_guid: string;
  storey_id: number | null;
  storey_guid: string | null;
  /** Along the host axis from its start, metres. */
  s_start: number;
  s_end: number;
  /** World x/y of `s_start` on the host axis; null when the host is not stored. */
  start: PlanXY | null;
  end: PlanXY | null;
  z_sill: number;
  z_head: number;
  is_external: boolean;
}

/** `SolidSlab` — one floor slab or roof piece of the building model (`GET /slabs`). */
export interface SolidSlab {
  /** 0-based position in the `solid_slabs` element index (not a row id). */
  id: number;
  /** Deterministic, storey-scoped (the guid the typed export gives the slab). */
  guid: string;
  kind: 'floor' | 'roof';
  /** `Storey.id` it belongs to; null when no stored storey has its idx. */
  storey_id: number | null;
  storey_guid: string | null;
  /** Which piece of its storey's floor (or roof); unique per (storey, kind). */
  part: number;
  polygon: {
    /** Outer ring, world x/y — open (the first point is not repeated). */
    outer: PlanXY[];
    holes: PlanXY[][];
  };
  /** World z of its lower face, metres. */
  z_base: number;
  /** World z of its upper face, metres. */
  z_top: number;
  /** Profile area, outer minus holes, m². */
  area: number;
  /** Of its mesh, m³. */
  volume: number;
  /** Its mesh is watertight. */
  closed: boolean;
}

// ----------------------------------------------- solid-model edits (ruxd-5uv) --
// POST /model/edits, /model/undo, /model/redo; GET /model/history,
// /model/revision, /model/snap; POST /model/rederive. World coordinates under
// the GET names; a value read and sent back round-trips.

/** The row kinds a model edit writes. */
export type ModelElementKind = 'storey' | 'wall' | 'opening' | 'space';

/**
 * One op of a batch (`ModelEditOp`): `<kind>.<verb>` — `create` + `record`,
 * `update` + `guid` + `set` (partial), `delete` + `guid` (a detected or
 * imported row is tombstoned), `revert` + `guid` (back to `edited_from`);
 * plus `wall.split` / `wall.merge` / `wall.join` and `ceiling.set`.
 */
export type ModelEditOp =
  | { op: `${ModelElementKind}.create`; record: Record<string, unknown>; joins?: Record<string, unknown>[] }
  | { op: `${ModelElementKind}.update`; guid: string; set: Record<string, unknown>; joins?: Record<string, unknown>[] }
  | { op: `${ModelElementKind}.delete`; guid: string }
  | { op: `${ModelElementKind}.revert`; guid: string }
  | { op: 'wall.split'; guid: string; s: number; new_guid?: string }
  | { op: 'wall.merge' | 'wall.join'; guids: [string, string] }
  | { op: 'ceiling.set'; guid: string; planes: { at: [number, number, number]; slope?: [number, number] }[] };

/** Body of `POST /model/edits`. */
export interface ModelEditRequest {
  /** The revision the batch was made against; omitted = not checked. */
  base_revision?: number;
  /** Shown in the history, e.g. "Flyt væg". */
  label?: string;
  /** Queue a `solids` job after the write (`rederive_job`). */
  rederive?: boolean;
  ops: ModelEditOp[];
}

/** One row a batch changed. */
export interface ModelChange {
  kind: ModelElementKind;
  guid: string;
  action: 'created' | 'updated' | 'deleted' | 'reverted' | 'restored';
}

/** `ModelEditResult` — the answer of an edit, undo or redo. */
export interface ModelEditResult {
  /** The model revision afterwards (the next `base_revision`). */
  revision: number;
  /** The edit-log entry applied, undone or redone. */
  edit_id: number;
  changes: ModelChange[];
  /** Left for the client to fix, e.g. an opening whose host wall the batch removed. */
  warnings: string[];
  /** The solids (`/slabs`, `/elements`) no longer match the model. */
  solids_stale: boolean;
  /** With `rederive: true`: the queued `solids` job's id (null when refused). */
  rederive_job?: string | null;
}

/** Body of `POST /model/undo` and `/model/redo`. */
export interface ModelUndoRequest {
  base_revision?: number;
  rederive?: boolean;
}

/** `ModelEditEntry` — one entry of the edit log. */
export interface ModelEditEntry {
  id: number;
  /** The revision the edit produced when applied. */
  revision: number;
  label: string;
  /** The account id that made it ("local" in local mode). */
  author: string;
  created_at: string;
  undone: boolean;
  changes: ModelChange[];
}

/** `GET /model/history`: the log, oldest first, with undo / redo state. */
export interface ModelHistory {
  revision: number;
  can_undo: boolean;
  can_redo: boolean;
  entries: ModelEditEntry[];
}

/** `GET /model/revision`. */
export interface ModelRevision {
  revision: number;
  can_undo: boolean;
  can_redo: boolean;
  solids_stale: boolean;
}

/** One detected vertical plane cut at the storey band, as a 2D segment (`ModelSnap.plane_traces`). */
export interface PlaneTrace {
  /** Label in the `planes` cloud. */
  plane_label: number;
  /** PlaneClass role: wall, furniture, clutter, ... */
  role: string;
  start: PlanXY;
  end: PlanXY;
  /** Metres. */
  length: number;
  /** Spread of its points across the trace, metres. */
  rms: number;
  point_count: number;
}

/** `GET /model/snap?storey=`: what the Geometri editor snaps to on one storey (world x/y). */
export interface ModelSnap {
  storey: number;
  storey_id: number | null;
  /** Degrees from world +x, counter-clockwise: `[a, a + 90]`; empty when nothing gives a direction. */
  axis_angles: number[];
  walls: { guid: string; start: PlanXY; end: PlanXY; thickness: number | null }[];
  endpoints: PlanXY[];
  plane_traces: PlaneTrace[];
}

/** `BuildingElement.category` — the typed export's element categories, in its order. */
export type ElementCategory = 'wall' | 'slab' | 'roof' | 'space' | 'door' | 'window' | 'opening';

/**
 * `BuildingElement` — one element of the typed building model with its own
 * closed mesh (`GET /elements`): the `solid_walls` / `solid_slabs` /
 * `solid_spaces` meshes split by their element index, and a box filling
 * each door, window and opening in its host wall (ReUseX io/export_scene).
 */
export interface BuildingElement {
  /** The element's GUID as the typed export names it (a slab's is derived, as `GET /slabs` gives it). */
  guid: string;
  category: ElementCategory;
  /** A space's room name; "" otherwise. */
  name: string;
  /** `Storey.id` it is on; null when no stored storey has its idx. */
  storey_id: number | null;
  storey_guid: string | null;
  /** A door's, window's or opening's host wall (`SolidWall.guid`); null for the others. */
  host_guid: string | null;
  /** Flat x, y, z, … in world coordinates, metres. */
  positions: number[];
  /** Flat triangles, 0-based vertex indices into `positions` / 3. */
  indices: number[];
}

/** `ComponentInfo` — one building component. */
export interface ComponentInfo {
  name: string;
  guid: string;
  type: string;
  /**
   * Parent **component**, -1 if none. Not a room: ReUseX does not associate a
   * component with a room, and this must not be presented as if it did.
   */
  parent_id?: number;
  /** -1 means manually created. */
  confidence?: number;
  vertex_count?: number;
  /**
   * Boundary area in m², derived on read (Newell) rather than stored. Absent
   * when the boundary has fewer than three vertices — so it is genuinely
   * optional and must never be rendered as `NaN`.
   */
  area?: number;
  /**
   * The segmentation instance this component came from (#211), lifted out of
   * the opaque `metadata` JSON. Absent for a manually created component.
   */
  source_instance_guid?: string;
}

/** `ComponentDetail` — a component plus its boundary polygon. */
export interface ComponentDetail extends ComponentInfo {
  /** Hessian normal form [a, b, c, d]. */
  plane?: number[];
  /** Boundary vertices as [x, y, z] triples. */
  vertices?: number[][];
  metadata?: string;
  notes?: string;
}

/**
 * `LabelLegend` — label id → display name, for one `Label` cloud.
 *
 * Both the body of `GET /clouds/{name}/labels` and the body of the `PATCH`,
 * which is why it is one type: the patch is a *sparse* legend, carrying only
 * the ids that changed. `"0"` never appears — 0 means unlabeled.
 */
export interface LabelLegend {
  labels: Record<string, string>;
}

/**
 * `MaterialPatch` — the body of `PATCH /materials/{guid}`.
 *
 * Sparse: only the properties that changed. A `null` value **deletes** the
 * property; a string sets it. Omitting a property leaves it untouched, which
 * is the whole reason this is not a PUT — passports carry MaterialEPAS fields
 * no GUI form models, and a PUT would silently drop them.
 */
export interface MaterialPatch {
  properties: Record<string, string | null>;
}

/** `MaterialInfo` — a material passport, listing shape. */
export interface MaterialInfo {
  id?: string;
  guid: string;
  property_count?: number;
  created_at?: string;
  version_number?: string;
}

/** `MaterialDetail` — a passport with its stored properties. */
export interface MaterialDetail extends MaterialInfo {
  linked_node_id?: number;
  properties?: Record<string, string>;
  /** Whether a thumbnail image is stored for this passport. */
  has_thumbnail?: boolean;
}

/** The kind of a user-defined material editor column. */
export type PropertyType =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'select'
  | 'multiselect'
  | 'url';

/**
 * `PropertyDefinition` — a user-defined column in the material editor.
 *
 * `options` is populated only for the `select`/`multiselect` types.
 */
export interface PropertyDefinition {
  id: string;
  name: string;
  type: PropertyType;
  options?: string[];
  sort_order: number;
  width?: number;
}

/** `MaterialCreate` — the (optional) body of `POST /materials`. */
export interface MaterialCreate {
  properties?: Record<string, string>;
}

/** `InstanceInfo` — one instance row of an instance-label cloud. */
export interface InstanceInfo {
  /** Label value in the instance cloud (>= 1). */
  instance_id: number;
  guid: string;
  semantic_class: number;
  point_count: number;
  /** Linked material passport, null when unlinked. */
  material_guid?: string | null;
  /**
   * Mean world position of the instance's finite points in the base cloud;
   * null when it cannot be computed. Absent from servers before ruxd-6tz
   * (CONTRACT-GAPS gap 8).
   */
  centroid?: WorldPoint | null;
  /** Axis-aligned world box of the same points; null as `centroid`. */
  bbox?: WorldBox | null;
}

/**
 * `VisibleFrame` — one entry from `/frames/visibility` or
 * `/instances/{cloud}/{id}/frames`.
 *
 * `centrality` is the sort key (ascending): 0 at the principal point
 * (most central), ~1 at a corner.  `score` is the higher-is-better
 * complement (`1 - centrality`).
 */
export interface VisibleFrame {
  frame_id: number;
  centrality: number;
  score: number;
  depth: number;
  u: number;
  v: number;
}

/** `ValidationIssue` — one finding of a stage's input-contract check. */
export interface ValidationIssue {
  /** Machine-readable check id, e.g. `missing_stage_input`. */
  check: string;
  message: string;
  severity: 'warning' | 'error';
  /** Terminal-formatted resolution, possibly multi-line. Empty when none. */
  hint: string;
  /** The named cloud/table this is about; empty when it is about nothing named. */
  artifact: string;
  /** The same resolution as `hint`, in order, as separate commands. */
  commands: string[];
}

export type ParameterType = 'number' | 'integer' | 'boolean' | 'string' | 'integer_list';

/** `StageParameter` — one knob of a runnable stage, as the server describes it. */
export interface StageParameter {
  /** The key to send inside `JobRequest.parameters`. */
  key: string;
  type: ParameterType;
  label: string;
  description: string;
  /** Null means the parameter is absent by default and has no neutral value. */
  default: number | boolean | string | null;
  minimum: number | null;
  maximum: number | null;
  /** True when sending the key at all changes behaviour, whatever its value. */
  presence_sensitive: boolean;
}

/** `StageInfo` — one entry of the stage catalogue. */
export interface StageInfo {
  stage: string;
  /** What this stage writes into `pipeline_log.stage`; empty when no runner. */
  log_name: string;
  summary: string;
  command: string;
  runnable: boolean;
  cancellable: boolean;
  ready: boolean;
  /** Artifacts this stage writes. */
  outputs: string[];
  /** Printable summary of the error-severity `issues`. */
  blockers: string[];
  issues: ValidationIssue[];
  /** Empty for a stage with no runner. */
  parameters: StageParameter[];
}

/** `PipelineLogEntry` — one durable stage-execution record. */
export interface PipelineLogEntry {
  id: number;
  stage: string;
  status: 'running' | 'success' | 'failed' | 'cancelled';
  started_at: string;
  /** Empty while still running. */
  finished_at?: string;
  /** Stage parameters JSON, as stored. Carries `job_id` for GUI-started runs. */
  parameters?: string;
  error_msg?: string;
}

export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

/** `JobProgress` — live progress of the phase a stage is currently in. */
export interface JobProgress {
  /** Machine-readable `core::Stage` token, e.g. `region_growing`. Match on this. */
  stage: string;
  /** Display form of the same phase. Never match on this. */
  stage_label: string;
  current: number;
  /** 0 means indeterminate — render a spinner, not a bar. */
  total: number;
  fraction?: number | null;
}

/** `Job` — one submitted stage run. */
export interface Job {
  id: string;
  project: string;
  stage: string;
  status: JobStatus;
  parameters?: Record<string, unknown>;
  error?: string;
  submitted_at: string;
  started_at?: string;
  finished_at?: string;
  cancel_requested?: boolean;
  progress?: JobProgress;
  /** What the job wrote; present only on `status: succeeded`. */
  result?: JobResult;
}

/** `JobArtifact` — one artifact a job wrote (`JobResult.outputs`). */
export interface JobArtifact {
  /** Which endpoint family it is fetched from. */
  kind: 'cloud' | 'mesh' | 'table';
  /** The stored name: `GET /clouds/{name}`, `/meshes/{name}`, or a table (`storeys`, `walls`, …). */
  name: string;
  /** Points or rows written; absent = not measured (never zero). */
  count?: number;
}

/** `JobResult` — what a succeeded job produced. */
export interface JobResult {
  /** The stage's own one-line account, for a human; never parse it. */
  summary: string;
  outputs: JobArtifact[];
}

/** `JobRequest` — the body of `POST /jobs`. */
export interface JobRequest {
  project?: string;
  stage: string;
  parameters?: Record<string, unknown>;
}

/**
 * `POST /job-chains` (ruxd-0sk): several stages as one action, queued in
 * order; a stage whose predecessor did not succeed is cancelled by the
 * server with the reason in `error`.
 */
export interface JobChainRequest {
  project?: string;
  stages: string[];
  /** Per-stage parameters keyed by stage token. */
  parameters?: Record<string, Record<string, unknown>>;
}

/** `ProjectSummary` — the dashboard payload, in one request. */
export interface ProjectSummary {
  /** File name only — the server never discloses its filesystem layout. */
  path: string;
  schema_version: number;
  projects: ProjectInfo[];
  clouds: CloudInfo[];
  meshes: MeshInfo[];
  sensor_frames: {
    total_count: number;
    segmented_count: number;
    width?: number;
    height?: number;
  };
  panoramic_images: {
    total_count: number;
    /** Panoramas linked to a sensor frame. */
    matched_count: number;
  };
  components: {
    total_count: number;
    count_by_type: Record<string, number>;
  };
  materials: MaterialInfo[];
}

// ----------------------------------------------------------- pose graph ----

/** One node in the pose graph: a sensor frame with a world pose. */
export interface PoseGraphNode {
  /** DB node_id (sensor frame id). */
  id: number;
  /** 4×4 world transform, column-major float64. */
  pose: number[];
}

/** Edge type produced by the optimizer. */
export type PoseGraphEdgeType = 'odometry' | 'loop_closure' | 'panorama';

/** One directed edge with its post-solve residual. */
export interface PoseGraphEdge {
  from: number;
  to: number;
  type: PoseGraphEdgeType;
  /** 0.5 × whitened squared residual after convergence (GTSAM). Low = satisfied. */
  residual: number;
  /** 1/σ² translational information weight at build time; absent if not extractable. */
  weight?: number;
}

/** Full pose graph response from `GET /api/v1/posegraph`. */
export interface PoseGraph {
  nodes: PoseGraphNode[];
  edges: PoseGraphEdge[];
}

/** Body of `POST /api/v1/posegraph/edges`. */
export interface PoseGraphEdgeCreate {
  from: number;
  to: number;
  type?: PoseGraphEdgeType;
  weight?: number;
}

/** Response of `DELETE /api/v1/posegraph/edges/{from}/{to}`. */
export interface PoseGraphEdgeDeleteResult {
  deleted: number;
  from: number;
  to: number;
  type: PoseGraphEdgeType | null;
}

// ------------------------------------- pose-graph editor (ruxd PR #19) ----
// The web scan-repair editor, first slice (ruxd-64g): blocks, the block
// transform (re-seed / local, preview), pose versions and the working pose
// edits. Every write is followed by a `posegraph.changed` event.

/** `PoseBlock` — a run of consecutive frames of one session (a "bone" in the editor). */
export interface PoseBlock {
  /** 1..n in node order; renumbered by every change. */
  id: number;
  name: string;
  first_node: number;
  last_node: number;
  /** The session (scans.id). */
  scan_id: number;
  /** Session ordinal 0, 1, … in node order (s0, s1, …). */
  session: number;
  /** Cut from the block before it: no odometry across the joint (dashed). */
  detached: boolean;
  frame_count: number;
  /** First camera position (world). */
  start?: [number, number, number];
  /** Last camera position (world). */
  end?: [number, number, number];
}

/** `PoseBlockInput` — one block of `PUT /posegraph/blocks`. */
export interface PoseBlockInput {
  name?: string;
  first_node: number;
  last_node: number;
  scan_id: number;
  /** Ignored by the server: detach with `POST /posegraph/blocks/{bid}/detach`. */
  detached?: boolean;
}

/** `PoseBlockList`. */
export interface PoseBlockList {
  blocks: PoseBlock[];
}

/** Why a block split is suggested (`BlockSplit.reason`). */
export type BlockSplitReason = 'session' | 'time_gap' | 'tracking_gap' | 'storey_change' | 'odometry_jump';

/** `BlockSplit` — one suggested split point. */
export interface BlockSplit {
  /** First frame of the suggested block. */
  node_id: number;
  prev_node_id: number;
  /** The strongest reason. */
  reason: BlockSplitReason;
  /** Every reason found at this node. */
  reasons: BlockSplitReason[];
  /** 0..1 rank. */
  score: number;
  gap_s: number;
  jump_m: number;
  /** One line for a tooltip. */
  detail: string;
}

/** `BlockSuggestions` — `GET /posegraph/blocks/suggest`. */
export interface BlockSuggestions {
  splits: BlockSplit[];
  /** Single frames that jump away and back (exclude them, do not split). */
  glitches: { node_id: number; jump_m: number }[];
  /** Camera-height bands found (m, ascending). */
  storey_levels: number[];
}

/** Query of `GET /posegraph/blocks/suggest`; omitted = the server default. */
export interface BlockSuggestQuery {
  time_gap_s?: number;
  tracking_gap_s?: number;
  jump_m?: number;
  storey_separation_m?: number;
  storey_min_frames?: number;
}

/**
 * `DetachResult` — `POST /posegraph/blocks/{bid}/detach`. The recorded
 * answer (ruxd PR #19 examples) carries the blocks without `session`,
 * `frame_count`, `start` and `end`, so a client refetches the list.
 */
export interface DetachResult {
  blocks: (Pick<PoseBlock, 'id' | 'name' | 'first_node' | 'last_node' | 'scan_id' | 'detached'> & Partial<PoseBlock>)[];
  prev_node_id: number;
  node_id: number;
  /** The reject_edge edit in the working pose edits. */
  edit_id: string;
  edge_rejected: boolean;
  /** Active loop edges between the block and the rest; 0 = nothing places it. */
  holding_loops: number;
  /** The pose version the detach / attach recorded. */
  version_id: number;
}

/**
 * `RigidDelta` — a world-frame rigid delta: `xyz` plus yaw / pitch / roll
 * (deg, R = Rz(yaw) Ry(pitch) Rx(roll)) about `pivot` (default: the
 * selection's camera centroid), or a full row-major `matrix` (wins).
 * p' = pivot + xyz + R (p − pivot).
 */
export interface RigidDelta {
  xyz?: [number, number, number];
  yaw_deg?: number;
  pitch_deg?: number;
  roll_deg?: number;
  pivot?: [number, number, number];
  matrix?: number[];
}

/** A hold's degrees of freedom. */
export type PoseDof = 'x' | 'y' | 'z' | 'roll' | 'pitch' | 'yaw';

/** `BlockTransformRequest` — `POST /posegraph/blocks/transform` (and `/blocks/{bid}/transform`). */
export interface BlockTransformRequest {
  /** Block ids. */
  blocks?: number[];
  /** Node ranges [first, last] (inclusive) or single node ids. */
  nodes?: (number | [number, number])[];
  delta: RigidDelta;
  /** Default `reseed`. */
  mode?: 'reseed' | 'local';
  /** Default 10. */
  blend_frames?: number;
  /** true (all DoFs, 5 cm / 1°) or the hold's DoF mask and sigmas. */
  hold?: boolean | { dofs?: PoseDof[]; sigma_trans_m?: number; sigma_rot_deg?: number };
  preview?: boolean;
  /** The pose version's label. */
  label?: string;
}

/** `BlockTransformJoint`. */
export interface BlockTransformJoint {
  /** Last moved frame at this end. */
  inside_node: number;
  /** Its unmoved neighbour (0 = none). */
  outside_node: number;
  session_boundary: boolean;
  detached: boolean;
  blend_frames: number;
  /** Camera distance across the joint before … */
  gap_before_m: number;
  /** … and after. */
  gap_after_m: number;
}

/** `RebuildOdometryResult` — `POST /posegraph/odometry/rebuild`. */
export interface RebuildOdometryResult {
  pairs: number;
  written: number;
  changed: number;
  replaced_imported: number;
  kept_confirmed: number;
  kept_rejected: number;
  cut_detached: number;
  snapshots_written: number;
  max_change_m: number;
  max_change_deg: number;
  version_id: number | null;
}

/** One moved frame of a preview (`BlockTransformResult.frames`). */
export interface BlockTransformFrame {
  node_id: number;
  /** Share of the delta (< 1 in a blend zone). */
  weight: number;
  shift_m: number;
  /** The new world pose, row-major 4×4. */
  after: number[];
}

/** `BlockTransformResult`. */
export interface BlockTransformResult {
  preview: boolean;
  /** The full delta, row-major 4×4. */
  delta: number[];
  pivot: [number, number, number];
  /** Frames moved by the full delta. */
  frames_moved: number;
  /** Frames in a blend zone. */
  frames_blended: number;
  max_shift_m: number;
  joints: BlockTransformJoint[];
  loops_touching: number;
  loops_disagreeing: number;
  odometry: RebuildOdometryResult | null;
  hold_edit_id: string | null;
  /** The pose version an apply recorded. */
  version_id: number | null;
  /** Preview only: every moved frame's new world pose. */
  frames?: BlockTransformFrame[];
}

/** `PoseVersion.kind`. */
export type PoseVersionKind = 'import' | 'baseline' | 'external' | 'apply' | 'odometry' | 'detach' | 'attach';

/** `PoseVersion` — one snapshot of the poses, the pose graph and the working edits. */
export interface PoseVersion {
  id: number;
  parent_id: number | null;
  /** `import | baseline | external | apply | odometry | detach | attach` (a string in the contract). */
  kind: string;
  label: string;
  /** The account id, "local" or "rux". */
  author: string;
  created_at: string;
  /** The step's result (an apply: BlockTransformResult without frames). */
  metrics: Record<string, unknown>;
  frame_count: number;
  edge_count: number;
  current: boolean;
}

/** `PoseVersionList` — `GET /posegraph/versions`, oldest first. */
export interface PoseVersionList {
  current: number | null;
  versions: PoseVersion[];
}

/** `PoseVersionChange` — what an undo / redo / adopt changed. */
export interface PoseVersionChange {
  from_version: number;
  to_version: number;
  frames_changed: number;
  max_shift_m: number;
  edges_changed: number;
}

/** `PoseVersionDiff` — `GET /posegraph/versions/{a}/diff/{b}`, largest shift first. */
export interface PoseVersionDiff {
  from: number;
  to: number;
  count: number;
  max_shift_m: number;
  frames: { node_id: number; shift_m: number; rotation_deg: number }[];
}

/** One edit of the working pose edits (`reusex.pose_edits.v1`); other fields pass through. */
export interface PoseEdit {
  id: string;
  kind: 'anchor' | 'move' | 'relative' | 'same_surface' | 'on_plan_wall' | 'reject_edge';
  [field: string]: unknown;
}

/** `PoseEditsDocument` — `GET|PUT /posegraph/edits`. */
export interface PoseEditsDocument {
  schema: 'reusex.pose_edits.v1';
  project_guid?: string;
  frame?: string;
  edits: PoseEdit[];
}

/** `POST /imports/rtabmap/{id}/complete`'s body. */
export interface RtabmapCompleteOptions {
  /** Default true; false imports as before (no duplicate check, no odometry rebuild, no version). */
  append_session?: boolean;
}

/** Body of `POST /api/v1/posegraph/icp` (#465). */
export interface IcpRefineRequest {
  from: number;
  to: number;
}

/**
 * Response of `POST /api/v1/posegraph/icp` (#465).
 *
 * `relative_pose` is a 16-element row-major 4×4 matrix T_to⁻¹ @ T_delta @ T_from
 * (maps a point from the "from" camera frame into the "to" camera frame).
 */
export interface IcpRefineResult {
  from: number;
  to: number;
  /** Row-major 4×4 relative pose (16 elements). */
  relative_pose: number[];
  /** RMS correspondence error after ICP (metres). Low = good alignment. */
  fitness: number;
  /** Fraction of source points within 5 cm of target after alignment. */
  inlier_fraction: number;
  /** True when ICP reached its convergence criterion. */
  converged: boolean;
}

// ------------------------------------------------- frame visibility (#453) ----

/**
 * `VisibleFrame` — one frame in which a queried point projects inside the image.
 *
 * Mirror of `components.schemas.VisibleFrame` in `docs/gui/openapi.yaml`.
 */
export interface VisibleFrame {
  /** DB node_id of the sensor frame. */
  frame_id: number;
  /** Normalised distance from principal point: 0 = dead centre, ~1 = corner. Sort key. */
  centrality: number;
  /** Higher-is-better complement: `1 - centrality`. */
  score: number;
  /** Point depth in the camera optical frame, metres (> 0). */
  depth: number;
  /** Projected pixel column (0 = left edge). */
  u: number;
  /** Projected pixel row (0 = top edge). */
  v: number;
}

// ---------------------------------------------------------- resource view ----

/**
 * `ViewRegion` — `[x, y, w, h]`, normalised 0..1 of the image (the equirect
 * for a panorama); a panorama's `x + w > 1` wraps across the seam.
 *
 * Mirror of `components.schemas.ViewRegion` in `docs/gui/openapi.yaml`.
 */
export type ViewRegion = [number, number, number, number];

/**
 * `ResourceView` — a resource's chosen image (`sys:view`, ReUseX schema
 * v27). `region: null` means the whole image. There is deliberately no
 * fallback to a ranked "best frame": a resource without a view is `null`.
 *
 * Mirror of `components.schemas.ResourceView`.
 */
export interface ResourceView {
  kind: 'frame' | 'panorama';
  /** `sensor_frames.node_id` for a `frame`, `panoramic_images.id` for a `panorama`. */
  id: number;
  region: ViewRegion | null;
}

/**
 * `FrameVisibilityList` — frames that see a world point, most central first.
 *
 * Mirror of `components.schemas.FrameVisibilityList`. The `count` is how many
 * `frames` carries (bounded by `limit`); `total` is the full visible count.
 */
export interface FrameVisibilityList {
  /** The queried world point [x, y, z] (a centroid for the instance variant). */
  point: [number, number, number];
  frames: VisibleFrame[];
  /** Number of frames in this response. */
  count: number;
  /** Total visible frames, before the `limit` cap. */
  total: number;
}

// ------------------------------------------------- frame-pair inspection ----

/**
 * Feature-descriptor algorithm for pairwise frame matching.
 *
 * Backend implementation reference: `tools/gt_curator/opencv_features.py`.
 * Used both in the `POST /frames/{a}/descriptor-match/{b}` request body and
 * in the result, so both sides of the wire speak the same string.
 */
export type DescriptorMethod = 'orb' | 'sift' | 'akaze';

/**
 * `DescriptorMatchResult` — response of `POST /frames/{a}/descriptor-match/{b}`.
 *
 * **Backend endpoint not yet implemented as of #446.** The GUI stub will
 * receive a 404 and surface it gracefully. The schema below is the contract
 * the implementation must satisfy:
 *
 *   POST /api/v1/frames/{frame_a}/descriptor-match/{frame_b}
 *   Body:    { method: DescriptorMethod }
 *   Success: DescriptorMatchResult (200 OK)
 *   Error:   { error: string }   (400 bad frame id, 404 frame not found,
 *                                 422 no depth for backprojection, 500 server)
 *
 * Matching pipeline (mirrors opencv_features.py):
 *  1. Load stored colour images for both frames.
 *  2. Extract keypoints and descriptors with the requested detector.
 *  3. BF-match + Lowe ratio test (threshold 0.85).
 *  4. Back-project matched 2-D points into 3-D using the stored depth image
 *     and sensor intrinsics; discard pairs where either depth is invalid.
 *  5. RANSAC rigid-body estimation (threshold 0.10 m, 500 iterations).
 *  6. Return keypoints, inlier mask, relative transform, and RMS error.
 */
export interface DescriptorMatchResult {
  frame_a: number;
  frame_b: number;
  method: DescriptorMethod;
  /** RANSAC-surviving inlier count. */
  n_inliers: number;
  /** RMS of inlier reprojection errors in metres; null when fewer than 3 inliers. */
  rms_m: number | null;
  /** All matched pixel coordinates in frame A (Lowe-filtered, including outliers). */
  keypoints_a: [number, number][];
  /** Matched pixel coordinates in frame B, index-aligned with keypoints_a. */
  keypoints_b: [number, number][];
  /** True for RANSAC inliers (index-aligned with keypoints_a/b). */
  inlier_mask: boolean[];
  /**
   * Row-major 4×4 rigid transform T_AB that maps 3-D points from frame B's
   * optical coordinate frame into frame A's — the loop-edge convention:
   * `p_A ≈ T_AB @ p_B`.  Null when RANSAC failed or fewer than 3 depth pairs.
   */
  transform: number[] | null;
  /** Non-null only when the server could not compute matches. */
  error: string | null;
}

// ------------------------------------------------------------ segment (#409) ----

/**
 * Wire tuple for one SAM3 bounding-box hint.
 *
 * Shape: `["pos" | "neg", [x1, y1, x2, y2]]` in image pixel coordinates.
 * The polarity marks whether the enclosed region is a positive example
 * (include) or a negative example (exclude).
 */
export type FrameSegmentBox = ['pos' | 'neg', [number, number, number, number]];

/** One SAM3 text + optional box prompt. */
export interface FrameSegmentPrompt {
  /**
   * Open-vocabulary class name, e.g. `"wall"`. May be `""` when the prompt
   * has a box or a point: the server then sends SAM3 the geometry-only text
   * `"visual"`, and the response's `labels` entry reads `"visual"`.
   */
  text: string;
  /** Bounding-box hints, each tagged with a polarity; a `pos` box keeps the objects it covers. */
  boxes?: FrameSegmentBox[];
  /** Click points `[x, y]` in image pixels; each keeps the object under it. */
  points?: [number, number][];
  /** Per-prompt threshold override; negative (absent) → use top-level confidence. */
  confidence?: number;
}

/** Body of `POST /frames/{id}/segment`. */
export interface FrameSegmentRequest {
  // No `model_path`: the GUI always uses the server's managed SAM3 model
  // (`GET /models/sam3/status`), so the field is deliberately not modelled.
  /** Empty / absent ⟹ use the model's built-in default class list. */
  prompts?: FrameSegmentPrompt[];
  /** Global detection threshold [0, 1]. Default 0.5. */
  confidence?: number;
  /** Write the label map back to the project. Default true. */
  save?: boolean;
  /**
   * Use CUDA/TensorRT inference. Absent ⟹ server-wide default
   * (`--segment-cuda` / `--no-segment-cuda`). Pass `false` on CPU-only
   * hosts to route inference through the ONNX CPU backend (#467).
   */
  use_cuda?: boolean;
}

/** Response of `POST /frames/{id}/segment`. */
export interface FrameSegmentResult {
  frame_id: number;
  labeled_pixels: number;
  saved: boolean;
  /**
   * Label id → class name. Populated from `prompts`; empty when the model's
   * built-in default list was used.
   */
  labels: Record<string, string>;
  /**
   * True when box/point prompts reached SAM3's geometry encoder; false when
   * none were sent or they were only clipped to (ONNX, text-only engines).
   * Absent from an older server.
   */
  geometry_prompts_used?: boolean;
  /** Fingerprint of the saved mask; null when not saved. Echo it in `SegmentResourceRequest`. */
  mask_revision?: string | null;
}

/** Provisioning state of the managed SAM3 model. */
export type Sam3ModelState = 'absent' | 'not_built' | 'downloading' | 'building' | 'ready' | 'error';

/** Body of `GET /models/sam3/status` — a pure probe; it never starts preparation. */
export interface Sam3ModelStatus {
  state: Sam3ModelState;
  /** Best-effort progress within the current phase, 0..1. */
  progress: number;
  message: string;
  use_cuda: boolean;
  /** Set when `state` is `ready`. */
  model_path?: string;
  /**
   * In `not_built`: engines built from an older recipe that the next
   * preparation rebuilds, when that is all it needs (a one-time update, no
   * download). Empty or absent for a first-time download/build.
   */
  update_engines?: string[];
}

/** Body of `POST /frames/{id}/segment/resource`. */
export interface SegmentResourceRequest {
  /** Prompt index in the frame's saved segmentation (API encoding, >= 0). */
  mask_label: number;
  /** Semantic class name; trimmed by the server, must not be blank. */
  class_name: string;
  /** Survey type for the new part; omitted/null ⟹ chosen by class. Never a rejected type (422). */
  type_id?: number | null;
  /** The run's `mask_revision`; a mask overwritten since is refused with 409. */
  mask_revision?: string | null;
}

/** `201` response of `POST /frames/{id}/segment/resource`. */
export interface SegmentResourceResult {
  resource_code: string;
  type_id: number;
  type_created: boolean;
  instance_id: number;
  instance_guid: string;
  point_count: number;
  /** Class id in `labels` (a CloudL value, >= 1). */
  label_id: number;
  label_created: boolean;
  /** Clouds the call rewrote (`labels`, `instances`). */
  clouds: string[];
  /** The new part's view: this frame, region = the mask's bbox padded 15 %. */
  view: ResourceView | null;
}

// ------------------------------------------------------- panorama segment ----

/** Body of `POST /panoramas/{id}/segment`. */
export interface PanoramaSegmentRequest {
  // No `model_path` — the managed SAM3 model is used (see FrameSegmentRequest).
  /** Text-only prompts; empty / absent ⟹ model's built-in default class list. */
  prompts?: Array<{ text: string; confidence?: number }>;
  /** Global detection threshold [0, 1]. Default 0.5. */
  confidence?: number;
  /** Number of equator tiles around the sphere. Default 8. */
  n_yaw?: number;
  /** Per-tile horizontal FOV in degrees. Default 90. */
  fov_deg?: number;
  /** Write the equirect label map back to the project. Default true. */
  save?: boolean;
  /**
   * Use CUDA/TensorRT inference. Absent ⟹ server-wide default
   * (`--segment-cuda` / `--no-segment-cuda`). Pass `false` on CPU-only
   * hosts to route inference through the ONNX CPU backend (#467).
   */
  use_cuda?: boolean;
}

/** Response of `POST /panoramas/{id}/segment`. */
export interface PanoramaSegmentResult {
  pano_id: number;
  labeled_pixels: number;
  saved: boolean;
  /** Label id → class name. Empty when the model's built-in list was used. */
  labels: Record<string, string>;
}

// ----------------------------------------------------------------- survey ----

/** `Treatment` — waste-hierarchy step (affaldshierarki), best first. */
export type Treatment =
  | 'bevaring'
  | 'genbrug'
  | 'genanvendelse'
  | 'nyttiggoerelse'
  | 'bortskaffelse';

/** `Treatment` values in waste-hierarchy order, best first. */
export const TREATMENTS = [
  'bevaring',
  'genbrug',
  'genanvendelse',
  'nyttiggoerelse',
  'bortskaffelse',
] as const satisfies readonly Treatment[];

/** `ReviewStatus` — a survey type's place in the review workflow. */
export type ReviewStatus = 'queue' | 'approved' | 'rejected';

/**
 * `EnvironmentStatus` — derived from a survey type's linked samples, never
 * stored. `afventer` blocks approval.
 */
export type EnvironmentStatus = 'ren_screening' | 'afventer' | 'forurenet' | 'ren_proevesvar';

/** Stage of an environmental sample's lab workflow. */
export type SampleStage = 'planlagt' | 'udtaget' | 'sendt' | 'svar';

/** Lab result of an environmental sample, once answered. */
export type SampleResult = 'ren' | 'forurenet';

/** `SurveyPart` — one bygningsdel (building part) filed under a survey type. */
export interface SurveyPart {
  code: string;
  type_id: number;
  cloud: string | null;
  instance_id: number | null;
  room_id: number | null;
  room_name: string;
  /**
   * `Storey.id` of the part's room (`GET /storeys`); null when it has none.
   * Derived, read-only. Required by the contract; absent from servers before
   * ruxd-6tz.
   */
  storey_id?: number | null;
  quantity: number;
  starred: boolean;
  note: string;
  material_guid: string | null;
  instance_guid: string | null;
  orphaned: boolean;
  /**
   * Marked as an error ("Markér som fejl"): still listed, but left out of
   * the type's quantity, the room and sample roll-ups and the report;
   * reversible with `PATCH {dismissed: false}`. Required by the contract;
   * absent from servers before ruxd-6tz (which can only DELETE a part).
   */
  dismissed?: boolean;
  /**
   * The model element the part is measured on (a wall, an opening, a slab
   * piece; `core::measure_survey`, ReUseX schema v37). Read-only. This and
   * the geom/mass fields below are absent from servers before v37.
   */
  element_guid?: string | null;
  /** Measured quantity in `geom_unit`; null before the first measure. Read-only. */
  geom_quantity?: number | null;
  geom_unit?: 'm²' | 'm³' | 'stk' | null;
  /** How `geom_quantity` was measured. */
  geom_source?: GeomSource | null;
  /** The part's tonnes from its type's build-up (null for stk, no build-up, or a unit that cannot be massed). */
  mass_t?: number | null;
  mass_method?: string | null;
  provenance?: Provenance;
}

/** `SurveyPart.geom_source`: how a part's quantity was measured in the model. */
export type GeomSource = 'solid_volume' | 'wall_area' | 'opening_area' | 'slab_area' | 'plane_area' | 'count';

/** Where one value came from (`value_provenance`, ReUseX schema v37; contract gap 2). */
export type ProvenanceKind = 'scan' | 'measured' | 'calculated' | 'entered' | 'suggested';

/** One `Provenance` entry. */
export interface FieldProvenance {
  kind: ProvenanceKind;
  /** e.g. `measure:wall_area`, `buildup:b3 (areal × tykkelse × densitet)`, `user`. */
  source: string;
  confidence: number | null;
  updated_at: string | null;
}

/**
 * `Provenance` — where each value came from, keyed by field (`quantity`,
 * `mass_t`, `buildup_id`, `eak_code`, …). A field with no entry has no
 * recorded provenance.
 */
export type Provenance = Record<string, FieldProvenance>;

/** `BuildupLayer` — one layer of a build-up. */
export interface BuildupLayer {
  material: string;
  /** Thickness [m]; >= 1 reads as massive (a volume in m³). */
  t?: number;
  /** Density [kg/m³]. */
  rho?: number;
  /** Mass per area [kg/m²], instead of t × rho. */
  kg_m2?: number;
  eak: string;
  /** Type name a split-off layer gets. */
  name?: string;
}

export type BuildupCategory = 'ydervaeg' | 'indervaeg' | 'daek' | 'tag';

/** `Buildup` — one Opbygning of the project's library (`GET /buildups`). */
export interface Buildup {
  id: string;
  category: BuildupCategory;
  name: string;
  main: BuildupLayer;
  others: BuildupLayer[];
  note?: string;
  origin: 'library' | 'project';
  /** Σ t of the layers measured by thickness. */
  layered_thickness_m: number;
}

/** `SurveyMeasureReport` — what `POST /survey/measure` measured (every count optional). */
export interface SurveyMeasureReport {
  parts_seen?: number;
  wall_parts?: number;
  slab_parts?: number;
  roof_parts?: number;
  opening_parts?: number;
  count_parts?: number;
  types_created?: number;
  parts_created?: number;
  quantities_seeded?: number;
  elements_missing?: number;
  buildups_suggested?: number;
  walls_with_thickness?: number;
  parts_without_geometry?: number;
}

/** `SurveyType` — one Kortlægning group row, with its parts. */
export interface SurveyType {
  id: number;
  name: string;
  eak_code: string;
  eak_name: string;
  bim7aa_code: string;
  unit: string;
  treatment: Treatment;
  review_status: ReviewStatus;
  confidence: number | null;
  /**
   * Effective tonnes: `mass_override_t` when set, else `mass_calc_t` (ReUseX
   * schema v37). On an older server, the stored value itself.
   */
  mass_t: number | null;
  /**
   * Tonnes from the build-up over the non-dismissed parts' quantities; null
   * without a build-up, for stk types, or without counted parts. This and
   * the mass/buildup fields below are absent from servers before v37 —
   * the frontend then computes the tonnes itself (`bygning/buildups`).
   */
  mass_calc_t?: number | null;
  /** The user's tonnes. PATCH `mass_t` sets it; `mass_t: null` clears it. */
  mass_override_t?: number | null;
  mass_source?: 'override' | 'calculated' | 'none';
  mass_method?: string | null;
  mass_warnings?: string[];
  /** Chosen build-up (`GET /buildups`). */
  buildup_id?: string | null;
  /** Set on the types `core::measure_survey` seeds from the typed model. */
  element_category?: BuildupCategory | null;
  provenance?: Provenance;
  /**
   * The type's colour under the Materiale lens, `#rrggbb` (a `--type-N`
   * palette slot's light value); null = the automatic one. Absent on a ruxd
   * that does not keep it yet (proposed, ruxd-6tz.13) — the frontend then keeps
   * it in localStorage (`bygning/typeColors`).
   */
  color?: string | null;
  note: string;
  starred: boolean;
  semantic_class: number;
  environment_status: EnvironmentStatus;
  sample_ids: number[];
  /** Sum of the non-dismissed parts' quantities. */
  quantity: number;
  parts: SurveyPart[];
  created_at: string;
  updated_at: string;
}

/** `SurveyCounts` — review-workflow counts. `all` is queue + approved. */
export interface SurveyCounts {
  queue: number;
  approved: number;
  rejected: number;
  all: number;
}

/** Body of `GET /survey`. */
export interface Survey {
  types: SurveyType[];
  counts: SurveyCounts;
}

/** `SurveySummary` — KPIs for Overblik and the Kortlægning coverage notice. */
export interface SurveySummary {
  counts: SurveyCounts;
  /** Tonnes per treatment over non-rejected types. */
  circularity: Record<Treatment, number>;
  total_mass_t: number;
  /** (bevaring + genbrug) / total. */
  reuse_share: number | null;
  pending_samples: number;
  /** Non-rejected types whose miljøstatus is forurenet. */
  contaminated_types: number;
  unlabeled_points: number | null;
  /** Share (0..1) of instance-cloud points with a label; null without an instance cloud. */
  classified_share: number | null;
  rooms_without_parts: string[];
}

/** One row of `SurveyFractions.fractions`. */
export interface SurveyFraction {
  eak_code: string;
  name: string;
  treatment: Treatment;
  mass_t: number;
  /** Tonnes from forurenet types; never merged with clean tonnes. */
  contaminated: boolean;
}

/** One row of `SurveyFractions.blocking`. */
export interface SurveyBlockingType {
  type_id: number;
  name: string;
  eak_code: string;
  treatment: Treatment;
  mass_t: number | null;
  /**
   * `sample`: awaiting a sample (blocks even when approved); `review`: not
   * approved yet; `mass`: approved but tonnage unknown. Precedence
   * sample > review > mass.
   */
  reason: 'review' | 'sample' | 'mass';
}

/**
 * `SurveyFractions` — approved tonnes per EAK code, treatment and
 * contamination for waste reporting. `bevaring` never counts.
 */
export interface SurveyFractions {
  fractions: SurveyFraction[];
  total_t: number;
  /** Length of `blocking`. */
  blocking_types: number;
  /** Non-rejected types that keep the report from being sent, in type-id order. */
  blocking: SurveyBlockingType[];
  /** True when `blocking` is empty. */
  ready: boolean;
}

/** `PartPhotos` — one survey part's photo evidence (`GET /survey/photos`). */
export interface PartPhotos {
  /**
   * Occlusion-aware: posed sensor frames whose depth image confirms the
   * instance centroid or one of 8 surface samples of the instance (within
   * 0.15 m); a frame without a depth image falls back to the frustum-only
   * test.
   */
  count: number;
  /** The most central of them; null when `count` is 0. */
  best_frame_id: number | null;
}

/**
 * `SurveyPhotos` — `GET /survey/photos`: photo evidence for every
 * instance-backed part, keyed on part code. Parts without an instance link
 * are absent.
 */
export interface SurveyPhotos {
  parts: Record<string, PartPhotos>;
}

/** `InstancePanorama` — one placeable 360 panorama near an instance. */
export interface InstancePanorama {
  panorama_id: number;
  /** Matched sensor frame, -1 if none. */
  node_id: number;
  /** Panorama centre to the instance centroid, metres. */
  distance: number;
  /** Equirect column of the centroid, 0..1 left to right. */
  u: number;
  /** Equirect row of the centroid, 0..1 from the top (north pole). */
  v: number;
  /** `levelled` = heading unknown, so `u` is only as good as that guess. */
  heading: 'resected' | 'levelled';
}

/** `InstancePanoramaList` — `GET /instances/{cloud}/{id}/panoramas`. */
export interface InstancePanoramaList {
  point: [number, number, number];
  cloud: string;
  instance_id: number;
  max_distance: number;
  /** Nearest first. */
  panoramas: InstancePanorama[];
  total: number;
}

/** `Sample` — one environmental sample, with its linked survey types. */
export interface Sample {
  id: number;
  code: string;
  title: string;
  what: string;
  stage: SampleStage;
  result: SampleResult | null;
  type_ids: number[];
  /**
   * Survey parts the sample covers, sorted. A part link rolls up to the
   * part's current type's miljøstatus, like a type link. Absent from servers
   * before sample part links (frontend-r46).
   */
  part_codes?: string[];
  created_at: string;
  updated_at: string;
}

/** Body of `POST /survey/sync` — counts of what sync_survey changed. */
export interface SurveySyncReport {
  types_created: number;
  parts_created: number;
  parts_existing: number;
  /** Instance rows sync considered; 0 means the instance cloud holds none. */
  instances_seen: number;
  /** Rows sync wrote first for an instance cloud from before schema v10. */
  instances_backfilled: number;
  rooms_assigned: boolean;
  parts_orphaned: number;
  orphaned_codes: string[];
  /** Instance links put back for instance-backed parts that lost theirs (a `create instances` re-run cascade-deletes them). */
  links_restored?: number;
  /** Instances skipped because the user deleted their part (schema v26 tombstones); absent from older servers. */
  parts_dismissed?: number;
  /** Instance-backed parts given a view from their best frame; absent from older servers. */
  views_attached?: number;
  /** Instance-backed parts sync could not place in any frame, so still without a view. */
  views_unavailable?: number;
  /** `core::measure_survey`'s counts, when sync ran it (`measure`, ReUseX schema v37). */
  measure?: SurveyMeasureReport;
}

/** Body of `DELETE /survey/types/<id>`. */
export interface SurveyTypeDeletion {
  parts_deleted: number;
  /** Scan-backed parts among them, tombstoned so a sync does not re-create them. */
  instances_dismissed: number;
}

/** Body of `POST /survey/types`. */
export interface SurveyTypeCreate {
  name: string;
  eak_code?: string;
  bim7aa_code?: string;
  unit?: string;
  treatment?: Treatment;
  note?: string;
  mass_t?: number | null;
  /** In [0, 1]. */
  confidence?: number | null;
  starred?: boolean;
  /** The build-up (`GET /buildups`); a server before v37 ignores it. */
  buildup_id?: string | null;
}

/**
 * `SurveyTypePatch` — sparse update for `PATCH /survey/types/{id}`; only
 * present fields change. `review_status: 'approved'` is refused (422) while
 * the type's derived `environment_status` is `afventer`.
 */
export interface SurveyTypePatch {
  name?: string;
  eak_code?: string;
  bim7aa_code?: string;
  unit?: string;
  note?: string;
  treatment?: Treatment;
  review_status?: ReviewStatus;
  confidence?: number | null;
  /** Sets the override (recorded as `entered`); null clears it and the calculated mass applies (v37). */
  mass_t?: number | null;
  /** Choose (or clear with null) the build-up; 422 for an id `GET /buildups` does not list. */
  buildup_id?: string | null;
  starred?: boolean;
  /** Redistributes across the type's existing parts (core::set_type_quantity). */
  quantity?: number;
  /** `#rrggbb`, or null for the automatic colour. Only sent to a server that returns `color`. */
  color?: string | null;
}

/** `SurveyPartPatch` — sparse update for `PATCH /survey/parts/{code}`. */
export interface SurveyPartPatch {
  /** Re-files the part under a different survey type. */
  type_id?: number;
  quantity?: number;
  starred?: boolean;
  note?: string;
  room_name?: string;
  /** Mark the part as an error (true) or restore it (false); see `SurveyPart.dismissed`. */
  dismissed?: boolean;
}

/** `SurveyTypeBatchPatch` — body of `PATCH /survey/types`: one patch for several types, one transaction. */
export interface SurveyTypeBatchPatch {
  ids: number[];
  patch: SurveyTypePatch;
}

/** Body of `POST /survey/types/{id}/merge`'s 200. */
export interface SurveyTypeMergeResult {
  /** The merged-away type, now deleted. */
  source_id: number;
  /** The type that remains. */
  into: SurveyType;
  /** Codes of the re-filed parts, ascending. */
  parts_moved: string[];
  /** Samples whose type link moved to `into`. */
  samples_relinked: number[];
}

/** Body of `POST /samples`. */
export interface SampleCreate {
  title: string;
  what?: string;
  type_ids?: number[];
  part_codes?: string[];
}

/** Body of `PUT /samples/{id}/links`; `part_codes` absent leaves the part links unchanged. */
export interface SampleLinksPut {
  type_ids: number[];
  part_codes?: string[];
}

/**
 * `SamplePatch` — sparse update for `PATCH /samples/{id}`. `result` can only
 * be set once `stage` is `svar`; `result: null` clears it.
 */
export interface SamplePatch {
  title?: string;
  what?: string;
  stage?: SampleStage;
  result?: SampleResult | null;
}

// ------------------------------------------------- resources & templates ----

/** Where a key's write lands (spec §4.3): `type` changes every part of the type. */
export type ResourceKeyScope = 'type' | 'part';

/**
 * Which editor a key gets in Kortlægning (spec §6.1). `multiselect` is a
 * leksikon `EnumArray` field (R3-A1): Phase 3 shows its joined values
 * read-only, with no editor of its own yet.
 */
export type ResourceDataType = 'text' | 'number' | 'enum' | 'multiselect' | 'boolean' | 'date' | 'view';

/**
 * `ResourceKey` — one row of `GET /resources/keys`. `id` is `lex:<guid>`
 * (leksikon), `col:<id>` (user column) or `sys:<name>` (built-in); the UI
 * shows `label`, never the id.
 */
export interface ResourceKey {
  id: string;
  label: string;
  category: string;
  scope: ResourceKeyScope;
  data_type: ResourceDataType;
  unit: string | null;
  /** Choices of an `enum` or `multiselect` key, wire values (e.g. `genbrug`). */
  options: string[];
  /** False for derived keys (`sys:environment`): a write is a 400. */
  editable: boolean;
}

/** `Resource` — one survey part's values by key id. `null` = no value. */
export interface Resource {
  code: string;
  type_id: number;
  /** Added by hand (no instance); only these can be deleted. */
  manual: boolean;
  values: Record<string, string | null>;
  /** The chosen image; always present, even when a template leaves `sys:view` out of `values`. */
  view: ResourceView | null;
}

/** Response of `PATCH /resources/{code}`: the resource, plus the type's other parts after a type-scoped write. */
export interface ResourcePatchResult {
  resource: Resource;
  siblings: Resource[];
}

/** Body of `POST /resources` — a manual part under an existing type. */
export interface ResourceCreate {
  type_id: number;
  name?: string;
}

/** Body of `POST /resources/columns` (a `PropertyDefinition` without its id). */
export interface ResourceColumnCreate {
  name: string;
  type: PropertyType;
  options?: string[];
  sort_order?: number;
  width?: number;
}

/** A template member: a whole category, or one key (spec §5.1). */
export type TemplateMember = { category: string } | { key: string };

/** The tag of a seeded template (spec §5.3). */
export type TemplateSeed = 'materialepas' | 'screening';

/** CSV export options saved on a template (Phase 4 edits them). The server keeps unknown fields. */
export interface TemplateCsv {
  delimiter?: string;
  encoding?: string;
  header?: 'label' | 'key';
  [extra: string]: unknown;
}

/** `Template` — one row of `GET /templates`, resolved against the live catalogue. */
export interface Template {
  id: number;
  name: string;
  members: TemplateMember[];
  csv: TemplateCsv;
  seed: TemplateSeed | null;
  /** Ordered key ids the members resolve to now (spec §5.2). */
  resolved_keys: string[];
  /** Members whose key or category no longer exists. */
  missing: TemplateMember[];
  created_at: string;
  updated_at: string;
}

/** Body of `POST /templates` (name required) or `PATCH /templates/{id}` (sparse). */
export interface TemplatePatch {
  name?: string;
  members?: TemplateMember[];
  csv?: TemplateCsv;
}

/** Body of `POST /templates`: {@link TemplatePatch} with `name` required. */
export type TemplateCreate = TemplatePatch & { name: string };

/** Camera placement for `GET /renders`. */
export type RenderView = 'plan' | 'top' | 'front' | 'orbit';

/** Query parameters of `GET /renders`. */
export interface RenderQuery {
  view?: RenderView;
  /** Which of 8 viewpoints on the orbit ring to render. Only used with `view: 'orbit'`. */
  orbit_index?: number;
  /** Layers to draw, in draw order. */
  layers?: string[];
  /** Instance id to paint; every other point is dimmed. */
  highlight_instance?: number;
  /** Named Label cloud supplying the instance id per point. Defaults to `instances`. */
  highlight_cloud?: string;
  width?: number;
  height?: number;
}

// --------------------------------------------------------------- reports ----

/** `ReportPdfVersion` — metadata for one stored Ressourcekortlægning PDF. */
export interface ReportPdfVersion {
  /** Stable numeric id; use in `GET /reports/ressourcekortlaegning/{id}`. */
  id: number;
  /** Generation time, UTC, as sqlite stores it ("2026-08-09 10:05:00"). Parse with `parseServerUtc`. */
  created_at: string;
  /** Human-readable label stored with the PDF. */
  label: string;
  /** Size of the PDF blob in bytes. */
  size_bytes: number;
  /** 1-based, generation order: the "v3" a user sees. */
  version: number;
  /** Types that blocked the report at generation; 0 = complete; null before schema v23. */
  blocking_types: number | null;
}

/**
 * `ReportSectionReadiness` — one section of the report as the server compiles
 * it (contract gap 4): whether it is ready and why not, in Danish, and the
 * 1-based page it starts on (null until compiled).
 */
export interface ReportSectionReadiness {
  /** `forside`, `sagsdata`, `ressourcer`, `miljoe`, `bilag`, `nedrivningsplan`, then template-only ones (`materialepas`). */
  id: string;
  /** Null for a section only the template has (it is always ready). */
  title: string | null;
  ready: boolean;
  /** "Sagsdata mangler: Matrikel, Bygherre"; empty when ready. */
  missing: string[];
  first_page: number | null;
}

/** One page image of a {@link ReportPreview}. */
export interface ReportPreviewPage {
  /** 0-based. */
  index: number;
  /** Server-absolute path of the PNG (`…/preview/{digest}/pages/{index}?ppi=N`). */
  url: string;
}

/**
 * `ReportPreview` — the Ressourcekortlægning report compiled to PNG pages
 * (`GET /reports/ressourcekortlaegning/preview`, 202 while compiling).
 */
export interface ReportPreview {
  /** 16 hex digits over the template, the data and every image: equal digests are the same pages. */
  digest: string;
  status: 'compiling' | 'ready' | 'failed';
  /** typst's message when failed. */
  error: string | null;
  ppi: number;
  /** Null until ready. */
  page_count: number | null;
  /** In page order; empty until ready. */
  pages: ReportPreviewPage[];
  /** One per section, in document order; filled in even while compiling. */
  sections: ReportSectionReadiness[];
  /** Null while compiling. */
  compile_ms: number | null;
}

/**
 * `ReportCover` — what the report's Forside shows (`GET /reports/cover`):
 * a frame or panorama (+ region), an upload, or `source: null` = the model
 * render.
 */
export interface ReportCover {
  source: 'frame' | 'panorama' | 'upload' | null;
  /** `sensor_frames.node_id` or `panoramic_images.id`; null for an upload or none. */
  id: number | null;
  region: ViewRegion | null;
  /** An upload's type. */
  mime_type: string | null;
}

/**
 * Parses a server timestamp into a `Date`. Two shapes are accepted:
 * - sqlite's zone-less UTC (`"YYYY-MM-DD HH:MM:SS"`, seconds optional), as
 *   `ReportPdfVersion.created_at` is stored;
 * - ISO 8601 with an explicit zone (`"…T10:05:00Z"`, `"…T12:05:00+02:00"`,
 *   optional fraction).
 *
 * `new Date(s)` must never be used on these strings: browsers parse a
 * space-separated, zone-less timestamp as **local** time, not UTC, which
 * silently shifts the displayed instant by the viewer's offset. So both shapes
 * go through `Date.UTC`, with an ISO offset applied by hand. Returns `null`
 * for zone-less ISO and for anything else that does not match.
 */
export function parseServerUtc(value: string): Date | null {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):(\d{2})(?::(\d{2}))?|T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|([+-])(\d{2}):(\d{2})))$/.exec(
      value.trim(),
    );
  if (!match) return null;
  const [, year, month, day, sh, smin, ss, ih, imin, is, frac, zone, sign, oh, om] = match;
  const iso = zone !== undefined;
  const f = {
    y: Number(year),
    mo: Number(month) - 1,
    d: Number(day),
    h: Number(iso ? ih : sh),
    mi: Number(iso ? imin : smin),
    s: Number(iso ? is : (ss ?? 0)),
  };
  // Date.UTC rolls an impossible field over (2026-02-30 -> 2026-03-02), so
  // only accept the instant when every field comes back as written.
  const local = new Date(Date.UTC(f.y, f.mo, f.d, f.h, f.mi, f.s));
  if (
    local.getUTCFullYear() !== f.y ||
    local.getUTCMonth() !== f.mo ||
    local.getUTCDate() !== f.d ||
    local.getUTCHours() !== f.h ||
    local.getUTCMinutes() !== f.mi ||
    local.getUTCSeconds() !== f.s
  ) {
    return null;
  }
  let offsetMin = 0;
  if (iso && zone !== 'Z') {
    const [hh, mm] = [Number(oh), Number(om)];
    if (hh > 23 || mm > 59) return null;
    offsetMin = (sign === '-' ? -1 : 1) * (hh * 60 + mm);
  }
  const ms = local.getTime() + (frac ? Math.round(Number(`0.${frac}`) * 1000) : 0) - offsetMin * 60_000;
  return Number.isNaN(ms) ? null : new Date(ms);
}

// ------------------------------------------------------------- websocket ----
// docs/gui/websocket-events.md + docs/gui/events.schema.json.

/** Server → client message types. Treat an unknown `type` as ignorable. */
export type EventType =
  | 'hello'
  | 'job.submitted'
  | 'job.started'
  | 'job.progress'
  | 'job.finished'
  | 'clouds.changed'
  | 'survey.changed'
  | 'panoramas.changed'
  | 'posegraph.changed'
  | 'model.changed'
  | 'error';

/** The `hello` handshake, sent once immediately on connect. */
export interface HelloEvent {
  type: 'hello';
  timestamp: string;
  api_version: string;
  implementation: string;
  project: string;
  /** Every currently known job, most recent first. */
  jobs: Job[];
}

/** A job lifecycle/progress event. Order these by `seq`, never by arrival. */
export interface JobEvent {
  type: 'job.submitted' | 'job.started' | 'job.progress' | 'job.finished';
  /** Assigned under the server's job lock, at the moment state changed. */
  seq: number;
  timestamp: string;
  project: string;
  job: Job;
}

/** The server rejected a client message. */
export interface ErrorEvent {
  type: 'error';
  timestamp: string;
  error: string;
}

/**
 * Clouds were rewritten outside a pipeline job (e.g. by
 * `POST /frames/{id}/segment/resource`). Sent to every connection regardless
 * of `subscribe`; carries no `seq` and no `job`.
 */
export interface CloudsChangedEvent {
  type: 'clouds.changed';
  timestamp: string;
  project: string;
  names: string[];
}

/**
 * A survey, sample or resource write committed through the API. `seq`
 * counts these per open case (a gap means one was missed); an absent list
 * means the scope is unknown (reload that kind), an empty one means none.
 */
export interface SurveyChangedEvent {
  type: 'survey.changed';
  timestamp: string;
  project: string;
  seq: number;
  type_ids?: number[];
  part_codes?: string[];
  sample_ids?: number[];
}

/**
 * A panorama was placed by hand (`PATCH /panoramas/{id}`) or added
 * (`POST /panoramas`) through the API, once the write committed. `ids` are
 * the panoramas written. Sent to every connection; no `seq`, no `job`.
 */
export interface PanoramasChangedEvent {
  type: 'panoramas.changed';
  timestamp: string;
  project: string;
  ids: number[];
}

/**
 * A pose-graph editor write committed (ruxd PR #19): blocks (PUT / split /
 * merge / rename), detach, a transform apply, an odometry rebuild, undo /
 * redo / adopt, or edits. `version` is the case's current pose version
 * afterwards (null when it has none). No `seq`, no `job`.
 */
export interface PosegraphChangedEvent {
  type: 'posegraph.changed';
  timestamp: string;
  project: string;
  reason: 'blocks' | 'detach' | 'apply' | 'odometry' | 'undo' | 'redo' | 'adopt' | 'edits' | string;
  version: number | null;
}

/** Narrowing helper for `posegraph.changed`. */
export function isPosegraphChangedEvent(event: ServerEvent): event is PosegraphChangedEvent {
  return event.type === 'posegraph.changed' && typeof (event as PosegraphChangedEvent).reason === 'string';
}

/**
 * A solid-model write committed (ruxd-5uv): `POST /model/edits`, undo,
 * redo or an applied `POST /spaces/rebuild`. `revision` is the model
 * revision afterwards (the next `base_revision`); `kinds` the element kinds
 * whose rows changed. No `seq`, no `job`.
 */
export interface ModelChangedEvent {
  type: 'model.changed';
  timestamp: string;
  project: string;
  reason: 'edit' | 'undo' | 'redo' | 'rebuild_spaces' | string;
  revision: number;
  edit_id: number;
  kinds: ModelElementKind[];
}

/** Narrowing helper for `model.changed`. */
export function isModelChangedEvent(event: ServerEvent): event is ModelChangedEvent {
  return event.type === 'model.changed' && typeof (event as ModelChangedEvent).revision === 'number';
}

export type ServerEvent =
  | HelloEvent
  | PosegraphChangedEvent
  | ModelChangedEvent
  | JobEvent
  | ErrorEvent
  | CloudsChangedEvent
  | SurveyChangedEvent
  | PanoramasChangedEvent
  | { type: string };

/** Narrowing helper for `panoramas.changed`; a malformed `ids` is rejected. */
export function isPanoramasChangedEvent(event: ServerEvent): event is PanoramasChangedEvent {
  const ids = (event as PanoramasChangedEvent).ids;
  return event.type === 'panoramas.changed' && Array.isArray(ids) && ids.every((id) => Number.isInteger(id));
}

/** Narrowing helper for `survey.changed`. */
export function isSurveyChangedEvent(event: ServerEvent): event is SurveyChangedEvent {
  return event.type === 'survey.changed' && typeof (event as SurveyChangedEvent).seq === 'number';
}

/** Narrowing helper for `clouds.changed`; a malformed `names` is rejected. */
export function isCloudsChangedEvent(event: ServerEvent): event is CloudsChangedEvent {
  const names = (event as CloudsChangedEvent).names;
  return (
    event.type === 'clouds.changed' &&
    Array.isArray(names) &&
    names.every((n) => typeof n === 'string')
  );
}

/** Narrowing helper: does this envelope carry a `Job` and a `seq`? */
export function isJobEvent(event: ServerEvent): event is JobEvent {
  return (
    typeof (event as JobEvent).seq === 'number' &&
    typeof (event as JobEvent).job === 'object' &&
    (event as JobEvent).job !== null &&
    typeof (event as JobEvent).job.id === 'string'
  );
}

/** Narrowing helper for the connect handshake. */
export function isHelloEvent(event: ServerEvent): event is HelloEvent {
  return event.type === 'hello';
}

/** True once a job can no longer change state. */
export function isTerminal(status: JobStatus): boolean {
  return status === 'succeeded' || status === 'failed' || status === 'cancelled';
}
